import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import MarkdownText from '../../components/foundations/MarkdownText';
import useTypingAnimation from '../hooks/useTypingAnimation';
import { MESSAGE_ROLES, ARTIFACT_TYPES, ATTACHMENT_BLOCK_TYPE } from '../constants';
import css from './ChatMessage.css';
import ArtifactWithInsights from './artifacts/ArtifactWithInsights';
import AttachmentChip from './AttachmentChip';
import EphemeralUpdatesPanel from './EphemeralUpdatesPanel';
import Reasoning from './Reasoning';
import MessageFeedback from './feedback/MessageFeedback';
import ContextFilesDebug from './ContextFilesDebug';

const ANALYZING_PLACEHOLDER_KEY = 'STATIC.PAGES.ANALYSIS_CHAT.AGENT_ACTIVITY.ANALYZING_PLACEHOLDER';

const ROLE_LABELS = {
    USER: 'You',
    ASSISTANT: 'AI Assistant',
};

const TEXT_MESSAGE_TYPE = 'markdown';

const ChatMessage = ({
    message,
    sessionId,
    sessionType,
    isReadOnly = false,
    isAdminMode = false,
    onFeedbackSubmitted,
    onFeedbackFailed,
}) => {
    const isUser = message.role === MESSAGE_ROLES.USER;
    const isAssistant = message.role === MESSAGE_ROLES.ASSISTANT;
    const { isStreaming } = message;

    // Check if content is empty (for initial thinking state). When a reasoning
    // chunk has already arrived, the Reasoning panel's "Thinking…" label is
    // the better signal — don't render the bouncing-dot placeholder on top of it.
    const hasReasoning = Array.isArray(message.reasoning) && message.reasoning.length > 0;
    const isInitialThinking =
        message.isThinking &&
        !hasReasoning &&
        (!message.content || (Array.isArray(message.content) && message.content.length === 0));

    const formattedTime = useMemo(() => (message.createdAt ? new Date(message.createdAt).toLocaleTimeString() : ''), [
        message.createdAt,
    ]);

    // Get current markdown content for typing animation (only for last markdown block in streaming)
    const currentTextForAnimation = useMemo(() => {
        if (!isAssistant || !isStreaming || !Array.isArray(message.content)) return '';

        for (let i = message.content.length - 1; i >= 0; i--) {
            if (message.content[i].type === TEXT_MESSAGE_TYPE) {
                return message.content[i].data || '';
            }
        }
        return '';
    }, [isAssistant, isStreaming, message.content]);

    // Use typing animation hook for assistant messages
    const animatedText = useTypingAnimation(currentTextForAnimation, isAssistant && isStreaming);

    const contentBlocks = useMemo(() => {
        if (!Array.isArray(message.content)) return [];

        return message.content.map((block, index) => {
            const isLastTextBlock =
                isStreaming &&
                block.type === TEXT_MESSAGE_TYPE &&
                index === message.content.findLastIndex(b => b.type === TEXT_MESSAGE_TYPE);
            const { data, ...rest } = block;

            return {
                ...rest,
                data: isLastTextBlock ? String(animatedText || '') : data,
                id: block.id || `${message.id}-block-${index}`,
            };
        });
    }, [message.content, message.id, isStreaming, animatedText]);

    const userAttachmentBlocks = useMemo(() => {
        if (!isUser || !Array.isArray(message.content)) return [];
        return message.content.filter(b => b.type === ATTACHMENT_BLOCK_TYPE);
    }, [isUser, message.content]);

    const isFinished = message.isHistorical || Boolean(message.finishedAt);

    const fullRowWidgetIds = useMemo(() => {
        const ids = new Set();
        let run = [];
        const flushRun = () => {
            if (run.length % 2 === 1) ids.add(run[run.length - 1].id);
            run = [];
        };
        contentBlocks.forEach(block => {
            if (block.type === ARTIFACT_TYPES.WIDGET) {
                run.push(block);
            } else {
                flushRun();
            }
        });
        if (isFinished) flushRun();
        return ids;
    }, [contentBlocks, isFinished]);

    const hasErrorBlock = contentBlocks.some(block => block.type === ARTIFACT_TYPES.ERROR);
    const shouldShowFeedback =
        !isReadOnly && isAssistant && isFinished && !hasErrorBlock && contentBlocks.length > 0;

    return (
        <div className={classNames(css.chatMessage, { [css.user]: isUser, [css.assistant]: isAssistant })}>
            <div className={css.chatMessageHeader}>
                <div className={css.chatMessageAvatar}>
                    <Icon name={isUser ? 'user' : 'robot'} size={IconSizes.SMALL} />
                </div>
                <div className={css.chatMessageRole}>{isUser ? ROLE_LABELS.USER : ROLE_LABELS.ASSISTANT}</div>
                <div className={css.chatMessageTime}>{formattedTime}</div>
            </div>
            <div
                className={classNames(css.chatMessageContent, {
                    [css.multiAttachment]: userAttachmentBlocks.length > 1,
                })}
                style={
                    userAttachmentBlocks.length > 1 ? { '--attachmentCount': userAttachmentBlocks.length } : undefined
                }
            >
                {isAssistant && message.reasoning?.length > 0 && (
                    <Reasoning reasoning={message.reasoning} isMessageActive={Boolean(message.isThinking)} />
                )}
                {isInitialThinking ? (
                    <div className={css.chatMessageThinking}>
                        <span className={css.chatMessageThinkingDot} />
                        <span className={css.chatMessageThinkingText}>
                            <Translate id={ANALYZING_PLACEHOLDER_KEY} />
                        </span>
                    </div>
                ) : (
                    <>
                        {isUser && userAttachmentBlocks.length > 0 && (
                            <div className={css.attachmentsRow}>
                                {userAttachmentBlocks.map((block, index) => (
                                    <AttachmentChip
                                        key={block.content_url || `${message.id}-att-${index}`}
                                        name={block.name}
                                        kind={block.kind}
                                        sizeBytes={block.size_bytes}
                                        previewUrl={block.previewUrl}
                                        contentUrl={block.content_url}
                                        large
                                    />
                                ))}
                            </div>
                        )}
                        {isUser && message.content && (
                            <MarkdownText
                                content={
                                    Array.isArray(message.content)
                                        ? String(message.content.find(b => b.type === TEXT_MESSAGE_TYPE)?.data || '')
                                        : String(message.content || '')
                                }
                            />
                        )}
                        {isAssistant && message.ephemeralUpdates?.length > 0 && (
                            <EphemeralUpdatesPanel updates={message.ephemeralUpdates} />
                        )}
                        {isAssistant &&
                            contentBlocks.map(block => {
                                const isWidget = block.type === ARTIFACT_TYPES.WIDGET;
                                const blockClassName = isWidget
                                    ? classNames(css.widget, {
                                          [css.widgetFullRow]: fullRowWidgetIds.has(block.id),
                                      })
                                    : css.artifact;
                                return (
                                    <div key={block.id} className={blockClassName}>
                                        <ArtifactWithInsights artifact={block} />
                                    </div>
                                );
                            })}
                        {shouldShowFeedback && (
                            <MessageFeedback
                                runId={message.runId}
                                sessionId={sessionId}
                                sessionType={sessionType}
                                contentBlocks={contentBlocks}
                                onSubmitted={onFeedbackSubmitted}
                                onFailed={onFeedbackFailed}
                            />
                        )}
                    </>
                )}
                {isAssistant && isAdminMode && <ContextFilesDebug files={message.contextFiles} />}
            </div>
        </div>
    );
};

ChatMessage.propTypes = {
    message: PropTypes.shape({
        id: PropTypes.string.isRequired,
        runId: PropTypes.string,
        role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
        content: PropTypes.array, // Array of content blocks: [{type: 'markdown', data: '...'}, {type: 'code', data: {...}}, ...]
        createdAt: PropTypes.instanceOf(Date),
        isThinking: PropTypes.bool,
        isStreaming: PropTypes.bool,
        isHistorical: PropTypes.bool,
        finishedAt: PropTypes.number,
        ephemeralUpdates: PropTypes.array,
        reasoning: PropTypes.array,
        steps: PropTypes.array,
        contextFiles: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    sessionId: PropTypes.string,
    sessionType: PropTypes.string,
    isReadOnly: PropTypes.bool,
    isAdminMode: PropTypes.bool,
    onFeedbackSubmitted: PropTypes.func,
    onFeedbackFailed: PropTypes.func,
};

export default React.memo(ChatMessage);
